import { CreateE2DesignRequest } from 'libs/e1api/infographic';
import { createE2Design as createE2DesignApi } from 'libs/e1api/infographic';
import { useToast } from 'modules/common/components/Toast';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Hook to create a new e2 design from template or existing e2 design.
 *
 * @returns createE2Design function to create a new e2 design.
 * - Fn returns a promise that resolves to the new e2 design id if creation was successful.
 * - Will handle rendering loading, success, and error toasts.
 * - Includes handling of overlimit error for free users.
 * - Does not include MP tracking - should be added by the caller if necessary.
 */
export const useCreateE2Design = () => {
  const { t } = useTranslation('common_navbar', { useSuspense: false, keyPrefix: 'fileMenu' });
  const toast = useToast();

  /**
   * Create a new e2 design from template or existing e2 design.
   * Will handle overlimit error for free users.
   *
   * @param createE2RequestParams - Params to create a new e2 design. - to be passed to /create_e2 E1 endpoint
   * @param loadingToastString - String to display in loading toast
   * @param successToastString - String to display in success toast
   * @param errorToastString - String to display in error toast
   * @returns string - design id of new infograph
   */
  const createE2Design = useCallback(
    async ({
      createE2RequestParams,
      loadingToastString,
      successToastString,
      errorToastString,
    }: {
      createE2RequestParams: CreateE2DesignRequest;
      loadingToastString: string;
      successToastString: string;
      errorToastString: string;
    }) => {
      try {
        const createLoadingToastId = 'create-loading-toast';
        toast({
          id: createLoadingToastId,
          title: loadingToastString,
          status: 'info',
        });

        const createResult = await createE2DesignApi(createE2RequestParams);
        toast.close(createLoadingToastId);

        if (createResult.status === 'overlimit') {
          toast({
            status: 'error',
            title: t('overlimit.title'),
            description: t('overlimit.description'),
          });
          return null;
        }

        const { status, infographic } = createResult;
        const newInfographId = typeof infographic === 'string' ? infographic : infographic?.data?.id;
        if (status !== 'ok' || !newInfographId) {
          throw new Error('Failed to create a new design');
        }

        toast({ title: successToastString });
        return newInfographId;
      } catch (error) {
        console.error(error);
        toast({
          status: 'error',
          title: errorToastString,
        });
      }
      return null;
    },
    [t, toast],
  );

  return createE2Design;
};
